import { useState } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(...registerables);

interface UniversityData {
  name: string;
  percentage: number;
  total: number;
  students: number;
  completed: number;
}

interface ChartCardProps {
  selectedTopic: string;
  top10ByPercentage?: UniversityData[];
  top10ByCompleted?: UniversityData[];
}

const ChartCard = ({ 
  selectedTopic, 
  top10ByPercentage = [],
  top10ByCompleted = []
}: ChartCardProps) => {
  const [activeTab, setActiveTab] = useState<'percentage' | 'count'>('percentage');

  // Dinamik ma'lumotlardan foydalanish
  const universitiesWithPercentage = activeTab === 'percentage' 
    ? top10ByPercentage.map((u) => ({
        name: u.name,
        percentage: u.percentage.toFixed(1),
        participatedCount: u.completed,
        total: u.total
      }))
    : top10ByCompleted.map((u) => ({
        name: u.name,
        percentage: u.percentage.toFixed(1),
        participatedCount: u.completed,
        total: u.total
      }));

  // Prepare data based on active tab
  const chartData = activeTab === 'percentage'
    ? {
      labels: universitiesWithPercentage.map(u => u.name),
      datasets: [
        {
          label: 'Ishtirok foizi (%)',
          data: universitiesWithPercentage.map(u => parseFloat(u.percentage)),
          backgroundColor: 'rgba(35, 83, 71, 0.9)',
          borderRadius: 8,
          barThickness: 18,
          categoryPercentage: 0.65,
          borderSkipped: false
        }
      ]
    }
    : {
      labels: universitiesWithPercentage.map(u => u.name),
      datasets: [
        {
          label: 'Ishtirok etganlar soni',
          data: universitiesWithPercentage.map(u => u.participatedCount),
          backgroundColor: 'rgba(35, 83, 71, 0.9)',
          borderRadius: 8,
          barThickness: 18,
          categoryPercentage: 0.65
        }
      ]
    };

  // Get max value for count scale
  const maxCount = Math.max(...universitiesWithPercentage.map(u => u.participatedCount));
  const maxCountRounded = Math.ceil(maxCount / 50) * 50;

  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 500,
      easing: 'easeOutQuart' as const,
      delay: (context: any) => {
        let delay = 0;
        if (context.type === 'data' && context.mode === 'default') {
          delay = context.dataIndex * 20;
        }
        return delay;
      }
    },
    interaction: {
      intersect: false,
      mode: 'nearest' as const
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#1e293b',
        padding: 12,
        borderRadius: 8,
        titleColor: '#fff',
        bodyColor: '#fff',
        titleFont: {
          size: 14,
          weight: 600
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          title: function (context: any) {
            return context[0].label;
          },
          label: function (context: any) {
            if (activeTab === 'percentage') {
              return `Ishtirok foizi: ${context.parsed.x.toFixed(1)}%`;
            } else {
              return `Ishtirok etganlar: ${context.parsed.x.toLocaleString()}`;
            }
          },
          afterBody: function (context: any) {
            const index = context[0].dataIndex;
            const uni = universitiesWithPercentage[index];
            return [
              '',
              `Jami foydalanuvchilar: ${uni.total.toLocaleString()}`,
              `Qatnashganlar: ${uni.participatedCount.toLocaleString()}`,
              `Foiz: ${uni.percentage}%`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: activeTab === 'percentage' ? 100 : maxCountRounded,
        grid: {
          color: '#f1f5f9',
          drawBorder: false
        },
        border: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          },
          color: '#64748b',
          callback: function (value: any) {
            if (activeTab === 'percentage') {
              return value + '%';
            } else {
              return value.toLocaleString();
            }
          }
        }
      },
      y: {
        grid: {
          display: false
        },
        border: {
          display: false
        },
        ticks: {
          font: {
            size: 13,
            weight: 400,
            lineHeight: 1.4
          },
          color: '#1e293b',
          autoSkip: false,
          padding: 10,
          crossAlign: 'far' as const,
          callback: function (value: any, index: number): string | string[] {
            const label = universitiesWithPercentage[index]?.name || '';
            if (label && label.length > 80) {
              const words = label.split(' ');
              const lines: string[] = [];
              let currentLine = '';

              for (let i = 0; i < words.length; i++) {
                const word = words[i];
                if ((currentLine + word).length > 80) {
                  lines.push(currentLine.trim());
                  currentLine = word + ' ';
                } else {
                  currentLine += word + ' ';
                }
              }

              if (currentLine.trim()) {
                lines.push(currentLine.trim());
              }
              return lines;
            }
            return label;
          }
        }
      }
    }
  };

  return (
    <div className="chart-card">
      <div className="chart-header">
        <h3>Top 10 - Eng ko'p ishtirok etgan universitet talabalari</h3>
        <div className="chart-tabs">
          <button
            className={`tab-button ${activeTab === 'percentage' ? 'active' : ''}`}
            onClick={() => setActiveTab('percentage')}
          >
            Foiz kesimida
          </button>
          <button
            className={`tab-button ${activeTab === 'count' ? 'active' : ''}`}
            onClick={() => setActiveTab('count')}
          >
            Soni kesimida
          </button>
        </div>
      </div>
      <div className="chart-body">
        <Chart type='bar' data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ChartCard;
